import React from 'react';
import styled from 'styled-components';

const Panel = styled.div`
  position: fixed;
  right: 0;
  top: 70px;
  width: 400px;
  height: calc(100vh - 70px);
  background: linear-gradient(135deg, rgba(0, 20, 40, 0.98), rgba(20, 0, 40, 0.98));
  border-left: 3px solid #00ffff;
  overflow-y: auto;
  z-index: 1000;
  padding: 24px;
  animation: slideIn 0.3s ease;
  box-shadow: -10px 0 30px rgba(0, 255, 255, 0.2);

  @keyframes slideIn {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 255, 255, 0.1);
  }

  &::-webkit-scrollbar-thumb {
    background: #00ffff;
    border-radius: 4px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: 2px solid #00ffff;
  color: #00ffff;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: #00ffff;
    color: #000000;
    transform: rotate(90deg);
  }
`;

const Title = styled.h2`
  color: #00ffff;
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 8px 0;
  padding-right: 40px;
  line-height: 1.3;
  text-shadow: 0 0 10px rgba(0, 255, 255, 0.5);
`;

const MetaInfo = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  margin-bottom: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const MetaItem = styled.span`
  background: rgba(0, 255, 255, 0.1);
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid rgba(0, 255, 255, 0.3);
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  color: #00ffff;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 0 0 12px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid rgba(0, 255, 255, 0.3);
`;

const KeywordTag = styled.span`
  display: inline-block;
  background: rgba(0, 255, 255, 0.2);
  color: #00ffff;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 11px;
  margin-right: 8px;
  margin-bottom: 8px;
  border: 1px solid rgba(0, 255, 255, 0.4);
  font-weight: 600;
`;

const Abstract = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
`;

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const MetricLabel = styled.span`
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  font-weight: 600;
`;

const MetricValue = styled.span`
  color: #00ffff;
  font-size: 12px;
  font-weight: 700;
`;

const MetricBar = styled.div`
  width: 100%;
  height: 6px;
  background: rgba(0, 255, 255, 0.1);
  border-radius: 3px;
  overflow: hidden;
  margin-top: 4px;
`;

const MetricFill = styled.div`
  height: 100%;
  background: ${props => {
    if (props.value > 0.8) return '#39ff14';
    if (props.value > 0.5) return '#00ffff';
    return '#ff8c00';
  }};
  width: ${props => props.value * 100}%;
  transition: width 0.3s ease;
  box-shadow: 0 0 8px ${props => {
    if (props.value > 0.8) return '#39ff14';
    if (props.value > 0.5) return '#00ffff';
    return '#ff8c00';
  }};
`;

const InvariantGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;

const InvariantCard = styled.div`
  background: rgba(139, 0, 255, 0.1);
  border: 1px solid rgba(139, 0, 255, 0.3);
  padding: 12px;
  border-radius: 6px;
`;

const InvariantName = styled.div`
  color: #8b00ff;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 4px;
`;

const InvariantValue = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 10px;
  line-height: 1.4;
`;

const GeometryInfo = styled.div`
  background: rgba(255, 140, 0, 0.1);
  border: 1px solid rgba(255, 140, 0, 0.3);
  padding: 12px;
  border-radius: 6px;
`;

const GeometryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const GeometryLabel = styled.span`
  color: #ff8c00;
  font-weight: 600;
`;

const GeometryValue = styled.span`
  color: rgba(255, 255, 255, 0.9);
  font-weight: 700;
`;

const ExtensionList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ExtensionItem = styled.li`
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  padding: 6px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  &:last-child {
    border-bottom: none;
  }

  &::before {
    content: '▹';
    color: #39ff14;
    margin-right: 8px;
    font-weight: bold;
  }
`;

const NodeDetails = ({ node, onClose, onReadFullArticle }) => {
  if (!node) return null;

  const renderMetrics = () => {
    if (!node.metrics) return null;

    return Object.entries(node.metrics).map(([key, value]) => (
      <div key={key}>
        <MetricRow>
          <MetricLabel>{key.replace(/_/g, ' ')}</MetricLabel>
          <MetricValue>{typeof value === 'number' ? value.toFixed(2) : value}</MetricValue>
        </MetricRow>
        {typeof value === 'number' && (
          <MetricBar>
            <MetricFill value={Math.min(value, 1)} />
          </MetricBar>
        )}
      </div>
    ));
  };

  const renderInvariants = () => {
    if (!node.semantic_roots?.core_invariants) return null;

    const invariants = node.semantic_roots.core_invariants;
    return Object.entries(invariants).map(([key, value]) => (
      <InvariantCard key={key}>
        <InvariantName>{key}</InvariantName>
        <InvariantValue>
          {typeof value === 'object' 
            ? JSON.stringify(value).substring(0, 50) + '...'
            : String(value).substring(0, 50)}
        </InvariantValue>
      </InvariantCard>
    ));
  };

  return (
    <Panel>
      <CloseButton onClick={onClose}>×</CloseButton>

      <Title>{node.label || node.title}</Title>
      
      <MetaInfo>
        <MetaItem>📅 {node.date}</MetaItem>
        <MetaItem>🏷️ {node.group}</MetaItem>
        <MetaItem>🔄 Depth: {node.recursion_depth || 0}</MetaItem>
      </MetaInfo>

      <Section>
        <SectionTitle>Node ID</SectionTitle>
        <Abstract style={{ fontSize: '11px', fontFamily: 'monospace', color: '#00ffff' }}>
          {node.id}
        </Abstract>
      </Section>

      {node.abstract && (
        <Section>
          <SectionTitle>Abstract</SectionTitle>
          <Abstract>{node.abstract}</Abstract>
        </Section>
      )}

      {node.keywords && node.keywords.length > 0 && (
        <Section>
          <SectionTitle>Keywords</SectionTitle>
          <div>
            {node.keywords.map((keyword, i) => (
              <KeywordTag key={i}>{keyword}</KeywordTag>
            ))}
          </div>
        </Section>
      )}

      {node.metrics && (
        <Section>
          <SectionTitle>Metrics</SectionTitle>
          {renderMetrics()}
        </Section>
      )}

      {node.semantic_roots?.core_invariants && (
        <Section>
          <SectionTitle>ROOT-LD Core Invariants</SectionTitle>
          <InvariantGrid>
            {renderInvariants()}
          </InvariantGrid>
        </Section>
      )}

      {node.geometry && (
        <Section>
          <SectionTitle>Geometry</SectionTitle>
          <GeometryInfo>
            {node.geometry.drift_magnitude !== undefined && (
              <GeometryRow>
                <GeometryLabel>Drift Magnitude:</GeometryLabel>
                <GeometryValue>{node.geometry.drift_magnitude.toFixed(3)}</GeometryValue>
              </GeometryRow>
            )}
            {node.geometry.curvature_range !== undefined && (
              <GeometryRow>
                <GeometryLabel>Curvature Range:</GeometryLabel>
                <GeometryValue>{node.geometry.curvature_range.toFixed(3)}</GeometryValue>
              </GeometryRow>
            )}
            {node.geometry.complexity !== undefined && (
              <GeometryRow>
                <GeometryLabel>Complexity:</GeometryLabel>
                <GeometryValue>{node.geometry.complexity.toFixed(3)}</GeometryValue>
              </GeometryRow>
            )}
          </GeometryInfo>
        </Section>
      )}

      {node.semantic_roots?.adaptive_periphery?.extensions && 
       node.semantic_roots.adaptive_periphery.extensions.length > 0 && (
        <Section>
          <SectionTitle>Adaptive Periphery Extensions</SectionTitle>
          <ExtensionList>
            {node.semantic_roots.adaptive_periphery.extensions.slice(0, 5).map((ext, i) => (
              <ExtensionItem key={i}>{ext}</ExtensionItem>
            ))}
          </ExtensionList>
        </Section>
      )}

      <Section>
        <SectionTitle>Actions</SectionTitle>
        <button
          style={{
            background: 'transparent',
            border: '2px solid #8b00ff',
            color: '#8b00ff',
            padding: '10px 16px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            width: '100%',
            transition: 'all 0.2s',
            marginBottom: '12px'
          }}
          onMouseOver={(e) => {
            e.target.style.background = '#8b00ff';
            e.target.style.color = '#000000';
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 4px 12px rgba(139, 0, 255, 0.4)';
          }}
          onMouseOut={(e) => {
            e.target.style.background = 'transparent';
            e.target.style.color = '#8b00ff';
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = 'none';
          }}
          onClick={onReadFullArticle}
        >
          📖 Read Full Article
        </button>

        <button
          style={{
            background: 'transparent',
            border: '2px solid #00ffff',
            color: '#00ffff',
            padding: '10px 16px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            width: '100%',
            transition: 'all 0.2s'
          }}
          onMouseOver={(e) => {
            e.target.style.background = '#00ffff';
            e.target.style.color = '#000000';
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 4px 12px rgba(0, 255, 255, 0.4)';
          }}
          onMouseOut={(e) => {
            e.target.style.background = 'transparent';
            e.target.style.color = '#00ffff';
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = 'none';
          }}
          onClick={() => {
            window.open(`https://recursivearchitectureintelligence.com/research#${node.id}`, '_blank');
          }}
        >
          🔗 View on Website
        </button>
      </Section>
    </Panel>
  );
};

export default NodeDetails;